<template>
  <div class="wp-check-page">
    <!-- 헤더 -->
    <div class="form-group row">
      <label class="col-2 form-control-label"><span>작업 계획 번호</span></label>
      <div class="col-10 pt-2">
        <span style="font-weight:600;">{{ permit && permit.planId || '-' }}</span>
        <span v-if="permit" class="tag ml-10" :class="statusTagClass(permit.status)">
          {{ statusLabel(permit.status) }}
        </span>
      </div>
    </div>

    <!-- 작업 기본 정보 -->
    <div class="form-group row mt-20" v-if="permit">
      <label class="col-2 form-control-label"><span>작업 기본 정보</span></label>
      <div class="col-10 pt-2">
        <div class="result-tbl">
          <table class="tbl">
            <colgroup>
              <col width="140px"><col width="auto">
              <col width="140px"><col width="auto">
            </colgroup>
            <tbody>
              <tr>
                <th>작업 명</th><td>{{ permit.planName || '-' }}</td>
                <th>업체명</th><td>{{ permit.companyName || '-' }}</td>
              </tr>
              <tr>
                <th>작업 지휘자</th>
                <td>{{ permit.workManager || '-' }} <span class="text-888 ml-10">{{ permit.workManagerPhone || '' }}</span></td>
                <th>안전담당자</th>
                <td>{{ permit.safetyManager || '-' }} <span class="text-888 ml-10">{{ permit.safetyManagerPhone || '' }}</span></td>
              </tr>
              <tr>
                <th>전체 작업 일정</th><td>{{ formatPeriod(permit.fromDate, permit.toDate) }}</td>
                <th>작업 인원</th><td>{{ permit.workPeopleCount != null ? permit.workPeopleCount + '명' : '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- 작업 위치 -->
    <div class="form-group row mt-20" v-if="payload">
      <label class="col-2 form-control-label"><span>작업 위치</span></label>
      <div class="col-10 pt-2">
        <div class="mb-5"><strong>구분:</strong> {{ idcName || resolveLocName(permit && permit.idcCode) || (permit && permit.idcCode) || '-' }}</div>
        <div v-if="locationLabels.length">
          <span v-for="(l, i) in locationLabels" :key="i" class="tag line-type mr-6">{{ l }}</span>
        </div>
        <div class="mt-10" v-if="payload.locationDetail" style="white-space:pre-wrap;">
          <strong>상세 위치:</strong><br>{{ payload.locationDetail }}
        </div>
      </div>
    </div>

    <!-- 위험 작업 종류 -->
    <div class="form-group row mt-20" v-if="highRiskList.length || heavyWorkList.length">
      <label class="col-2 form-control-label"><span>위험 작업 종류</span></label>
      <div class="col-10 pt-2">
        <div v-if="highRiskList.length" class="mb-5">
          <strong class="mr-10">고위험 작업:</strong>
          <span v-for="t in highRiskList" :key="'hr-'+t" class="tag light-blue mr-6">{{ t }}</span>
        </div>
        <div v-if="heavyWorkList.length">
          <strong class="mr-10">중장비 작업:</strong>
          <span v-for="t in heavyWorkList" :key="'hw-'+t" class="tag warning mr-6">{{ t }}</span>
        </div>
      </div>
    </div>

    <!-- 작업 일정 (Gantt 형태) -->
    <div class="form-group row mt-20" v-if="payload && payload.scheduleRows && payload.scheduleRows.length">
      <label class="col-2 form-control-label"><span>예정 공정표</span></label>
      <div class="col-10 pt-2">
        <div class="wp-table-scroll">
        <table class="tbl wp-timeline" v-if="scheduleTimelineDays.length">
          <colgroup>
            <col width="120px">
            <col width="120px">
            <col width="100px">
            <col width="200px">
            <col width="200px">
            <col v-for="day in scheduleTimelineDays" :key="'day-col-'+day.key" :style="timelineDayColStyle">
          </colgroup>
          <thead>
            <tr>
              <th rowspan="2">구분</th>
              <th rowspan="2">관</th>
              <th rowspan="2">층</th>
              <th rowspan="2">상세위치</th>
              <th rowspan="2">작업항목</th>
              <th :colspan="scheduleTimelineDays.length" class="text-center">{{ scheduleTimelineLabel }}</th>
            </tr>
            <tr>
              <th v-for="day in scheduleTimelineDays" :key="'day-head-'+day.key" class="wp-timeline-day">{{ day.day }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in payload.scheduleRows" :key="'sch-'+i">
              <td>{{ resolveLocName(r.regionCode) || dash }}</td>
              <td>{{ resolveLocName(r.buildingCode) || dash }}</td>
              <td>{{ resolveLocName(r.floorCode) || dash }}</td>
              <td class="ellipsis">{{ resolveLocName(r.detailCode) || r.detailLocation || dash }}</td>
              <td class="ellipsis">{{ r.workItem || dash }}</td>
              <template v-if="getTimelineMeta(r).valid">
                <td v-if="getTimelineMeta(r).offset > 0" :colspan="getTimelineMeta(r).offset" class="wp-timeline-empty"></td>
                <td v-if="getTimelineMeta(r).length > 0" :colspan="getTimelineMeta(r).length" class="wp-timeline-span">
                  <div class="wp-timeline-bar" :style="{ backgroundColor: getTimelineColor(i) }"></div>
                </td>
                <td v-if="getTimelineMeta(r).remaining > 0" :colspan="getTimelineMeta(r).remaining" class="wp-timeline-empty"></td>
              </template>
              <td v-else :colspan="scheduleTimelineDays.length" class="wp-timeline-empty text-888">일정 미입력</td>
            </tr>
          </tbody>
        </table>
        <!-- 일자 범위 계산 불가 시(전체 일정 없음) fallback: 텍스트 표 -->
        <table class="tbl" v-else>
          <thead>
            <tr>
              <th>구분</th><th>관</th><th>층</th><th>상세위치</th>
              <th>작업항목</th><th>시작</th><th>종료</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in payload.scheduleRows" :key="'sch-fb-'+i">
              <td>{{ resolveLocName(r.regionCode) || dash }}</td>
              <td>{{ resolveLocName(r.buildingCode) || dash }}</td>
              <td>{{ resolveLocName(r.floorCode) || dash }}</td>
              <td>{{ resolveLocName(r.detailCode) || r.detailLocation || dash }}</td>
              <td>{{ r.workItem || dash }}</td>
              <td>{{ r.startDate || dash }}</td>
              <td>{{ r.endDate || dash }}</td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>
    </div>

    <!-- 위험성평가표 -->
    <div class="form-group row mt-20" v-if="payload && payload.rows && payload.rows.length">
      <label class="col-2 form-control-label"><span>위험성평가</span></label>
      <div class="col-10 pt-2">
        <div class="wp-table-scroll">
        <table class="tbl wp-check-risk-tbl">
          <thead>
            <tr>
              <th>작업항목</th><th>작업절차</th><th>위험요인</th><th>재해유형</th><th>보호구</th>
              <th>측정·장비</th><th>현재 안전조치</th><th>위험성</th><th>추가 안전조치</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(row, ri) in payload.rows">
              <tr v-for="(set, si) in (row.sets && row.sets.length ? row.sets : [{}])" :key="'rs-'+ri+'-'+si">
                <td v-if="si === 0" :rowspan="(row.sets && row.sets.length) ? row.sets.length : 1">{{ row.item || dash }}</td>
                <td>{{ set.procedure || dash }}</td>
                <td>{{ set.riskFactor || dash }}</td>
                <td>{{ getSelectLabel(set.hazard) || dash }}</td>
                <td>{{ getSelectLabel(set.protection) || dash }}</td>
                <td>{{ getSelectLabel(set.tool) || dash }}</td>
                <td>{{ set.currentSafety || dash }}</td>
                <td>
                  <span v-if="set.riskLevel" class="tag" :class="riskChipClass(set.riskLevel)">{{ set.riskLevel }}</span>
                  <span v-else>{{ dash }}</span>
                </td>
                <td>{{ set.additionalSafety || dash }}</td>
              </tr>
            </template>
          </tbody>
        </table>
        </div>
      </div>
    </div>

    <!-- ───────────────────────────────────────── -->
    <!--  선택된 허가서별 상세 (탭 UI)            -->
    <!-- ───────────────────────────────────────── -->
    <div class="form-group row mt-20" v-if="activePermits.length">
      <label class="col-2 form-control-label"><span>허가서별 상세</span></label>
      <div class="col-10 pt-2">
        <ul class="nav nav-tabs wp-hr-tabs">
          <li v-for="entry in activePermits" :key="'tab-'+entry.key" class="nav-item">
            <a href="javascript:void(0)"
               :class="{ active: activeTab === entry.key }"
               @click="activeTab = entry.key">
              {{ entry.label }}
            </a>
          </li>
        </ul>
        <div class="tab-content wp-detail-tab-content">
          <template v-for="entry in activePermits">
            <div v-if="activeTab === entry.key" :key="'pane-'+entry.key" class="tab-pane active wp-detail-pane">
              <permit-type-summary :perm-key="entry.key" :data="entry.data"></permit-type-summary>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- 산재보험가입증명서 -->
    <div class="form-group row mt-20">
      <label class="col-2 form-control-label"><span>산재보험가입증명서</span></label>
      <div class="col-10 pt-2">
        <table class="tbl" v-if="insuranceFiles.length">
          <colgroup>
            <col width="auto"><col width="120px"><col width="160px"><col width="100px">
          </colgroup>
          <thead>
            <tr><th>파일명</th><th>크기</th><th>업로드 일시</th><th>다운로드</th></tr>
          </thead>
          <tbody>
            <tr v-for="f in insuranceFiles" :key="f.id">
              <td>
                <a href="javascript:void(0)" class="under-line" @click="downloadFile(f)">{{ f.fileName }}</a>
              </td>
              <td>{{ formatFileSize(f.fileSize) }}</td>
              <td>{{ formatTs(f.uploadedAt) }}</td>
              <td>
                <button class="btn btn-sm line-2" @click="downloadFile(f)">
                  <i class="icon-download mr-5"></i>다운로드
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div v-else class="text-888">업로드된 파일이 없습니다.</div>
      </div>
    </div>

    <!-- 허가서 첨부파일 (전체 permitType 통합) -->
    <div class="form-group row mt-20">
      <label class="col-2 form-control-label"><span>허가서 첨부파일</span></label>
      <div class="col-10 pt-2">
        <div class="wp-table-scroll" v-if="attachmentFiles.length">
          <table class="tbl">
            <colgroup>
              <col width="150px"><col width="200px"><col width="auto"><col width="110px"><col width="160px"><col width="100px">
            </colgroup>
            <thead>
              <tr><th>구분</th><th>항목</th><th>파일명</th><th>크기</th><th>업로드 일시</th><th>다운로드</th></tr>
            </thead>
            <tbody>
              <tr v-for="f in attachmentFiles" :key="f.id">
                <td>{{ permitTypeName(f.permitType) }}</td>
                <td>{{ docLabel(f) }}</td>
                <td>
                  <a href="javascript:void(0)" class="under-line" @click="downloadFile(f)">{{ f.fileName }}</a>
                </td>
                <td>{{ formatFileSize(f.fileSize) }}</td>
                <td>{{ formatTs(f.uploadedAt) }}</td>
                <td>
                  <button class="btn btn-sm line-2" @click="downloadFile(f)">
                    <i class="icon-download mr-5"></i>다운로드
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="text-888">업로드된 파일이 없습니다.</div>
      </div>
    </div>

    <!-- ───── 담당자 검토 모드 전용 ───── -->

    <!-- 반려 사유 (REJECTED 시) -->
    <div class="form-group row mt-20" v-if="isReviewMode && permit && permit.status === 'REJECTED' && permit.rejectReason">
      <label class="col-2 form-control-label"><span>반려 사유</span></label>
      <div class="col-10 pt-2">
        <div class="alert alert-danger" style="white-space:pre-wrap;">{{ permit.rejectReason }}</div>
      </div>
    </div>

    <!-- 검토 의견 -->
    <div class="form-group row mt-20" v-if="isReviewMode">
      <label class="col-2 form-control-label"><span class="ess">검토 의견</span></label>
      <div class="col-10 pt-2">
        <textarea class="form-control" rows="4"
                  v-model="reviewerComment"
                  placeholder="NTREE 상신 전 검토 의견을 입력하세요"
                  :disabled="!canEditReview"></textarea>
        <div class="text-888 mt-5" style="font-size:11px;">상신 시 본 의견이 함께 저장됩니다.</div>
        <div v-if="isReviewMode && permit && !isOrderer" class="alert alert-warning mt-5" style="font-size:12px;">
          발주담당자만 검토 의견 작성·NTREE 상신·반려가 가능합니다. (전화번호는 마스킹되어 표시됩니다)
        </div>
      </div>
    </div>

    <!-- 신청 불가 사유 안내 (기존엔 버튼만 비활성이라 원인을 알 수 없었다) -->
    <div v-if="!isReviewMode && !loading" class="text-center mt-20">
      <span v-if="loadError" class="text-danger" style="font-size:12px;">
        작업 허가서 정보를 불러오지 못했습니다. 「다시 불러오기」를 눌러 주세요.
      </span>
      <span v-else-if="permit && permit.status !== 'DRAFT'" class="text-888" style="font-size:12px;">
        이미 신청된 허가서입니다. (현재 상태: {{ statusLabel(permit.status) }})
      </span>
    </div>

    <!-- 액션 버튼 -->
    <div class="btn-wrap mt-30 justify-content-center">
      <!-- 신청 모드 -->
      <template v-if="!isReviewMode">
        <button class="btn btn-lg line-2" @click="prev()">
          <i class="icon-arrow-line-left mr-5"></i>이전
        </button>
        <button v-if="loadError" class="btn btn-lg line-1" @click="retryLoad()">
          <i class="icon-refresh mr-5"></i>다시 불러오기
        </button>
        <button v-else class="btn btn-lg blue" :disabled="!canSubmit || submitting" @click="submit()">
          <span v-if="submitting">신청 중...</span>
          <span v-else>신청<i class="icon-check ml-5"></i></span>
        </button>
      </template>
      <!-- 검토 모드 -->
      <template v-else>
        <button class="btn btn-lg line-1" :disabled="!canEditReview || saving" @click="saveComment()">
          <span v-if="saving">저장 중...</span>
          <span v-else>의견 저장</span>
        </button>
        <button class="btn btn-lg danger" :disabled="!canEditReview || submitting" @click="rejectPermit()">
          <span v-if="submitting">처리 중...</span>
          <span v-else>반려</span>
        </button>
        <button class="btn btn-lg blue" :disabled="!canSubmitReview || submitting" @click="submitToNtree()">
          <span v-if="submitting">상신 중...</span>
          <span v-else>NTREE 상신<i class="icon-check ml-5"></i></span>
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import PermitTypeSummary from './components/PermitTypeSummary.vue';

export default {
  name: 'WorkPermitCheck',
  components: { PermitTypeSummary },
  data() {
    return {
      planId: '',
      // 링크 토큰(URL 의 ?k=). planId 열거 차단용 — 메일·NTREE 링크로 진입 시에만 존재
      linkToken: '',
      // 외부 작성자(비 SSO) 최종확인 진입 여부. true 면 SSO 예외 별칭 경로를 쓴다.
      isApplyFlow: false,
      // 단건 조회 실패 여부. 조회 실패와 '상태가 DRAFT 아님' 을 화면에서 구분하기 위해 둔다.
      // (예전엔 둘 다 신청 버튼 비활성으로만 보여서 원인을 알 수 없었다)
      loadError: false,
      permit: null,
      payload: null,
      loading: false,
      submitting: false,
      // 허가서 상세 탭 선택
      activeTab: '',
      // 검토 모드 전용
      reviewerComment: '',
      saving: false,
      // 산재보험가입증명서 파일 (BE 메타)
      insuranceFiles: [],
      // 허가서 첨부파일 전체 (BE 메타, insurance 제외)
      attachmentFiles: [],
      locationMap: {},
      idcName: '',
      dash: '-',
      scheduleTimelineColors: ['#2F80ED', '#27AE60', '#F2C94C', '#EB5757', '#9B51E0']
    };
  },
  computed: {
    isReviewMode() {
      return this.$route.name === 'workPermitReview';
    },
    canSubmit() {
      return !!this.permit && this.permit.status === 'DRAFT';
    },
    isOrderer() {
      // 서버가 내려주는 발주담당자 여부(전화번호 전체노출/상신 권한 판단)
      return !!this.permit && this.permit.viewerIsOrderer === true;
    },
    canEditReview() {
      // SUBMITTED 상태 + 발주담당자만 검토 의견 작성·상신·반려 가능
      return !!this.permit && this.permit.status === 'SUBMITTED' && this.isOrderer;
    },
    canSubmitReview() {
      return this.canEditReview;
    },
    highRiskList() { return (this.payload && this.payload.highRiskTargets) || []; },
    heavyWorkList() { return (this.payload && this.payload.heavyWorkTypes) || []; },
    locationLabels() {
      const keys = (this.payload && this.payload.selectedFloorKeys) || [];
      return keys.map((k) => k.split('|').map((c) => this.locationMap[c] || c).filter(Boolean).join(' / '));
    },
    activePermits() {
      if (!this.payload) return [];
      const tags = new Set([
        ...((this.payload.highRiskTargets) || []),
        ...((this.payload.heavyWorkTypes) || [])
      ]);
      const PERMIT_DEF = [
        { key: 'fireWorkPermit',      label: '화기작업',            tag: '화기작업' },
        { key: 'highAltitudePermit',  label: '고소작업',            tag: '고소작업' },
        { key: 'confinedSpacePermit', label: '밀폐공간',            tag: '밀폐공간' },
        { key: 'electricWorkPermit',  label: '전기작업',            tag: '전기작업' },
        { key: 'chemicalPermit',      label: '화학물질',            tag: '화학물질' },
        { key: 'heavyLiftPermit',     label: '중량물취급',          tag: '중량물취급' },
        { key: 'operatingWorkPermit', label: '운전중작업',          tag: '운전중작업' },
        { key: 'excavationPermit',    label: '굴착작업',            tag: '굴착작업' },
        { key: 'cargoEquipPermit',    label: '차량계하역운반기계',  tag: '차량계하역운반기계' },
        { key: 'mobileCranePermit',   label: '이동식크레인',        tag: '이동식크레인작업' },
        { key: 'constrEquipPermit',   label: '차량계건설기계',      tag: '차량계건설기계 작업' },
        { key: 'forkliftPermit',      label: '지게차',              tag: '지게차' },
        { key: 'boomLiftPermit',      label: '차량형 고소작업대',   tag: '차량형 고소작업대' },
        { key: 'scissorLiftPermit',   label: '시저형 고소작업대',   tag: '시저형 고소작업대' }
      ];
      // 사용자가 실제 체크한 위험작업(highRiskTargets / heavyWorkTypes)에 해당하는 양식만 노출.
      return PERMIT_DEF
        .filter((p) => tags.has(p.tag))
        .map((p) => ({ ...p, data: this.payload[p.key] || {} }));
    },
    scheduleTimelineRange() {
      if (!this.payload) return null;
      // permit.fromDate / toDate 우선
      if (this.permit && this.permit.fromDate && this.permit.toDate) {
        return {
          start: new Date(Number(this.permit.fromDate)),
          end: new Date(Number(this.permit.toDate))
        };
      }
      // scheduleRows 의 min/max 일자
      let minDate = null;
      let maxDate = null;
      (this.payload.scheduleRows || []).forEach((row) => {
        const start = this.parseDate(row.startDate);
        const end = this.parseDate(row.endDate);
        if (!start || !end) return;
        if (!minDate || start < minDate) minDate = start;
        if (!maxDate || end > maxDate) maxDate = end;
      });
      if (!minDate || !maxDate) return null;
      return { start: minDate, end: maxDate };
    },
    scheduleTimelineDays() {
      if (!this.scheduleTimelineRange) return [];
      const days = [];
      const start = new Date(this.scheduleTimelineRange.start);
      const end = new Date(this.scheduleTimelineRange.end);
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      for (let current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
        days.push({
          key: this.formatDate(current),
          day: current.getDate(),
          month: current.getMonth() + 1
        });
        if (days.length > 62) break;
      }
      return days;
    },
    scheduleTimelineLabel() {
      if (!this.scheduleTimelineRange) return '';
      const start = this.scheduleTimelineRange.start;
      const end = this.scheduleTimelineRange.end;
      const sameMonth = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
      if (sameMonth) {
        return `${start.getMonth() + 1}월`;
      }
      return `${start.getMonth() + 1}월 ${start.getDate()}일 ~ ${end.getMonth() + 1}월 ${end.getDate()}일`;
    },
    timelineDayColStyle() {
      const n = this.scheduleTimelineDays.length;
      if (!n) return { width: '28px' };
      return { width: `max(28px, ${(40 / n).toFixed(3)}vw)` };
    }
  },
  watch: {
    activePermits(newList) {
      // 활성 허가서 목록이 변하면 탭 자동 보정.
      const keys = newList.map((p) => p.key);
      if (keys.length === 0) {
        this.activeTab = '';
      } else if (!keys.includes(this.activeTab)) {
        this.activeTab = keys[0];
      }
    }
  },
  mounted() {
    this.planId = this.$route.query.planId || '';
    // 링크 토큰(k): 메일·NTREE 링크에 동봉되어 오는 planId 서명값.
    // 없으면(목록에서 진입 등) 서버가 신원 기반으로 판정한다.
    this.linkToken = this.$route.query.k || '';
    this.isApplyFlow = this.$route.query.flow === 'apply';
    Promise.all([this.loadLocations(), this.load(), this.loadAttachmentFiles()]);
  },
  methods: {
    /**
     * 조회 API base.
     * 외부 작성자는 SSO 가 없어 /api/private/** 에서 신원을 못 얻으므로,
     * Apache exceptURI 에 이미 포함된 /api/public/ 아래 별칭 경로를 쓴다.
     * 사내 검토(메일 링크·목록 진입)는 기존 경로 그대로 — 그래야 SSO 사번이 잡힌다.
     */
    wpBase() {
      return this.isApplyFlow ? '/api/public/work-permit/view' : '/api/private/work-permit';
    },
    async load(isRetry) {
      this.loading = true;
      try {
        const res = await this.$http.get(this.wpBase() + '/' + encodeURIComponent(this.planId),
          { params: this.linkToken ? { k: this.linkToken } : {} });
        const data = res && res.data ? (res.data.payload || res.data) : null;
        this.permit = data || null;
        if (this.permit && this.permit.payload) {
          try { this.payload = JSON.parse(this.permit.payload); }
          catch (e) { this.payload = null; }
        }
        if (this.permit && this.permit.idcCode) {
          this.idcName = this.locationMap[this.permit.idcCode] || '';
        }
        // 검토 모드: reviewerComment 초기값 (미작성 시 기본 확인 문구 프리필)
        this.reviewerComment = (this.permit && this.permit.reviewerComment) || '작업허가내용 확인하였습니다.';
        this.loadError = !this.permit;
      } catch (e) {
        console.log('work-permit 조회 실패', e);
        // 신청 흐름 토큰이 작성 중 만료되면 401 이 난다. workPermitCheck 라우트는 진입 시
        // 토큰 검증 대상이 아니라 만료 상태로도 들어올 수 있다. 갱신 후 1회만 재시도.
        if (!isRetry && await this.tryRefreshToken()) {
          this.loading = false;
          return this.load(true);
        }
        this.permit = null;
        this.loadError = true;
        this.noti('조회 오류', '작업 허가서 정보를 불러오지 못했습니다.', 'danger');
      } finally {
        this.loading = false;
      }
    },
    /** 신청 흐름 토큰 슬라이딩 갱신. 성공하면 true. */
    async tryRefreshToken() {
      try {
        const res = await this.$http.post('/api/public/work-permit/token/refresh');
        const data = res && res.data ? (res.data.payload || res.data) : null;
        if (data && data.token) {
          this.$store.commit('auth/setAuth', data.token);
          return true;
        }
      } catch (e) {
        console.log('토큰 갱신 실패', e);
      }
      return false;
    },
    /** 조회 실패 시 사용자가 직접 재시도 */
    retryLoad() {
      this.loadError = false;
      this.load();
    },
    async saveComment() {
      if (!this.canEditReview || this.saving) return;
      this.saving = true;
      try {
        await this.$http.post(
          '/api/private/work-permit/' + encodeURIComponent(this.planId) + '/reviewer-comment',
          { reviewerComment: this.reviewerComment }
        );
        this.noti('저장 완료', '검토 의견이 저장되었습니다.', 'success');
      } catch (e) {
        console.log('의견 저장 실패', e);
        this.noti('저장 오류', '검토 의견 저장 중 오류가 발생했습니다.', 'danger');
      } finally {
        this.saving = false;
      }
    },
    async submitToNtree() {
      if (!this.canSubmitReview || this.submitting) return;
      if (!this.reviewerComment || !this.reviewerComment.trim()) {
        this.noti('입력 필요', '검토 의견을 입력한 뒤 상신해 주세요.', 'warning');
        return;
      }
      this.submitting = true;
      try {
        const res = await this.$http.post(
          '/api/private/work-permit/' + encodeURIComponent(this.planId) + '/submit-ntree',
          { reviewerComment: this.reviewerComment }
        );
        const data = res && res.data ? (res.data.payload || res.data) : null;
        if (data && data.status === 'NTREE_PENDING') {
          this.permit = data;
          this.noti('상신 완료', 'NTREE에 결재 요청을 보냈습니다. 결재 결과가 도착하면 알림이 발송됩니다.', 'success');
        } else {
          this.noti('처리 결과', (res && res.data && res.data.message) || '결과를 확인할 수 없습니다.', 'warning');
        }
      } catch (e) {
        console.log('submit-ntree 실패', e);
        this.noti('상신 오류', 'NTREE 상신 중 오류가 발생했습니다.', 'danger');
      } finally {
        this.submitting = false;
      }
    },
    async rejectPermit() {
      if (!this.canEditReview || this.submitting) return;
      const reason = (this.reviewerComment || '').trim();
      if (!reason) {
        this.noti('입력 필요', '반려 사유(검토 의견)를 입력한 뒤 반려해 주세요.', 'warning');
        return;
      }
      if (!window.confirm('이 작업허가서를 반려하시겠습니까?\n반려 사유: ' + reason)) return;
      this.submitting = true;
      try {
        const res = await this.$http.post(
          '/api/private/work-permit/' + encodeURIComponent(this.planId) + '/reject',
          { rejectReason: reason }
        );
        const data = res && res.data ? (res.data.payload || res.data) : null;
        if (data && data.status === 'REJECTED') {
          this.permit = data;
          this.noti('반려 완료', '작업허가서를 반려했습니다. 신청자에게 반려 결과가 발송됩니다.', 'success');
        } else {
          this.noti('처리 결과', (res && res.data && res.data.message) || '결과를 확인할 수 없습니다.', 'warning');
        }
      } catch (e) {
        console.log('반려 실패', e);
        this.noti('반려 오류', '반려 처리 중 오류가 발생했습니다.', 'danger');
      } finally {
        this.submitting = false;
      }
    },
    async loadInsuranceFiles() {
      if (!this.planId) return;
      try {
        const res = await this.$http.get(
          this.wpBase() + '/' + encodeURIComponent(this.planId) + '/files/by-doc',
          { params: { permitType: 'insurance', docKey: 'insurance' } }
        );
        const data = (res && res.data && (res.data.payload || res.data)) || [];
        const list = Array.isArray(data) ? data : (data.rows || []);
        // 삭제된 파일 제외
        this.insuranceFiles = list.filter((f) => f && f.isDeleteYN !== 'Y');
      } catch (e) {
        console.log('insurance files 로드 실패', e);
      }
    },
    async loadAttachmentFiles() {
      if (!this.planId) return;
      try {
        const res = await this.$http.get(
          this.wpBase() + '/' + encodeURIComponent(this.planId) + '/files'
        );
        const data = (res && res.data && (res.data.payload || res.data)) || [];
        const list = Array.isArray(data) ? data : (data.rows || []);
        const alive = list.filter((f) => f && f.isDeleteYN !== 'Y');
        // 단일 소스(planId 전체 파일)에서 산재보험/그 외로 분리 — by-doc 별도 호출 불필요
        this.insuranceFiles = alive.filter((f) => f.permitType === 'insurance');
        this.attachmentFiles = alive.filter((f) => f.permitType !== 'insurance');
      } catch (e) {
        console.log('attachment files 로드 실패', e);
      }
    },
    // permitType(허가서 key) → 한글 구분명
    permitTypeName(pt) {
      const map = {
        fireWorkPermit: '화기작업', highAltitudePermit: '고소작업', confinedSpacePermit: '밀폐공간',
        electricWorkPermit: '전기작업', chemicalPermit: '화학물질', heavyLiftPermit: '중량물취급',
        operatingWorkPermit: '운전중작업', excavationPermit: '굴착작업', cargoEquipPermit: '차량계하역운반기계',
        mobileCranePermit: '이동식크레인', constrEquipPermit: '차량계건설기계', forkliftPermit: '지게차',
        boomLiftPermit: '차량형 고소작업대', scissorLiftPermit: '시저형 고소작업대', insurance: '산재보험'
      };
      return map[pt] || pt || '-';
    },
    // 첨부 항목(docKey) 라벨: payload 의 attachmentDocs 에서 매칭, 없으면 docKey
    docLabel(f) {
      try {
        const permit = this.payload && this.payload[f.permitType];
        const docs = permit && permit.attachmentDocs;
        if (Array.isArray(docs)) {
          const d = docs.find((x) => x && x.key === f.docKey);
          if (d && d.label) return d.label;
        }
      } catch (e) { /* noop */ }
      return f.docKey || '-';
    },
    downloadFile(f) {
      if (!f || !f.id) return;
      window.open(this.wpBase() + '/file/' + encodeURIComponent(f.id) + '/download', '_blank');
    },
    formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return '-';
      const n = Number(bytes);
      if (n < 1024) return n + ' B';
      if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
      if (n < 1024 * 1024 * 1024) return (n / (1024 * 1024)).toFixed(1) + ' MB';
      return (n / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
    },
    formatTs(ms) {
      if (!ms) return '-';
      return moment(Number(ms)).format('YYYY-MM-DD HH:mm');
    },
    async loadLocations() {
      try {
        const res = await this.$http.get('/api/public/safety-location/idcList');
        this.buildLocationMap(res.data || []);
        if (this.permit && this.permit.idcCode) {
          this.idcName = this.locationMap[this.permit.idcCode] || '';
        }
      } catch (e) {
        console.log('safety-location 로드 실패', e);
      }
    },
    buildLocationMap(nodes) {
      // Vue 2 반응성을 보장하기 위해 새 객체를 만들어 통째 교체.
      // (this.locationMap[code] = name 식으로 직접 키를 추가하면 computed 가 재실행되지 않음)
      const map = {};
      const walk = (arr) => {
        (arr || []).forEach((node) => {
          if (!node) return;
          if (node.isDeleteYN && node.isDeleteYN !== 'N') return;
          map[node.locationCode] = node.locationName;
          if (node.children && node.children.length) walk(node.children);
        });
      };
      walk(nodes);
      this.locationMap = map;
    },
    resolveLocName(code) {
      if (!code) return '';
      return this.locationMap[code] || '';
    },
    chipList(arr) {
      if (!arr || !arr.length) return this.dash;
      return arr.join(', ');
    },
    workTypesText(d) {
      const arr = (d && d.workTypes) || [];
      const out = [...arr];
      if (d && d.isOther && d.otherText) out.push('기타: ' + d.otherText);
      return out.length ? out.join(', ') : this.dash;
    },
    spaceTypesText(d) {
      const arr = (d && d.spaceTypes) || [];
      const out = [...arr];
      if (d && d.isOther && d.otherText) out.push('기타: ' + d.otherText);
      return out.length ? out.join(', ') : this.dash;
    },
    protectionsText(d) {
      const arr = (d && d.protections) || [];
      const out = [...arr];
      if (d && d.isProtectionOther && d.protectionOtherText) out.push('기타: ' + d.protectionOtherText);
      return out.length ? out.join(', ') : this.dash;
    },
    joinWithOther(arr, otherFlag, otherText) {
      const out = [...(arr || [])];
      if (otherFlag && otherText) out.push('기타: ' + otherText);
      return out.length ? out.join(', ') : this.dash;
    },
    fileList(arr) {
      if (!arr || !arr.length) return this.dash;
      return arr.map((f) => f.name || '').filter(Boolean).join(', ');
    },
    async submit() {
      if (!this.canSubmit || this.submitting) return;
      this.submitting = true;
      try {
        const body = Object.assign({}, this.payload || {}, {
          planId: this.permit.planId,
          planName: this.permit.planName,
          companyName: this.permit.companyName,
          workManager: this.permit.workManager,
          workManagerPhone: this.permit.workManagerPhone,
          workPeopleCount: this.permit.workPeopleCount,
          safetyManager: this.permit.safetyManager,
          safetyManagerPhone: this.permit.safetyManagerPhone,
          idcCode: this.permit.idcCode,
          fromDate: this.permit.fromDate,
          toDate: this.permit.toDate
        });
        const res = await this.$http.post('/api/public/work-permit?submit=true', body);
        const data = res && res.data ? (res.data.payload || res.data) : null;
        if (data && data.status === 'SUBMITTED') {
          this.permit = data;
          this.noti('접수 완료', '안전작업 허가서가 접수되었습니다. 담당자에게 메일·메신저로 알림이 발송되었습니다.', 'success');
        } else {
          this.noti('처리 결과', (res && res.data && res.data.message) || '결과를 확인할 수 없습니다.', 'warning');
        }
      } catch (e) {
        console.log('submit 실패', e);
        this.noti('신청 오류', '작업 허가서 신청 중 오류가 발생했습니다.', 'danger');
      } finally {
        this.submitting = false;
      }
    },
    prev() {
      this.$router.push({ path: '/app/workPermit/input' });
    },
    /**
     * 선택형 값(문자열 | 옵션객체 | 배열)을 표시용 라벨로.
     *
     * 보호구·작업도구는 다중선택이라 [{key,category,name}, ...] 배열로 저장된다.
     * 배열 분기가 없으면 typeof v === 'object' 에 걸린 뒤 v.label/name/value 가 모두
     * undefined 가 되어 빈 문자열 → '-' 로 표시된다(저장은 정상인데 안 보이던 원인).
     * 입력화면(WorkPermit.vue getSelectLabel/formatOptionLabel)과 같은 규칙을 쓴다.
     */
    getSelectLabel(v) {
      if (v == null) return '';
      if (typeof v === 'string') return v;
      if (Array.isArray(v)) {
        return v.map((x) => this.getSelectLabel(x)).filter(Boolean).join(', ');
      }
      if (typeof v === 'object') {
        const name = v.label || v.name || v.value || '';
        return v.category && name ? (v.category + ' / ' + name) : name;
      }
      return String(v);
    },
    parseDate(value) {
      if (!value) return null;
      const parts = String(value).split('-');
      if (parts.length !== 3) return null;
      const y = Number(parts[0]);
      const m = Number(parts[1]);
      const d = Number(parts[2]);
      if (!y || !m || !d) return null;
      const date = new Date(y, m - 1, d);
      if (Number.isNaN(date.getTime())) return null;
      date.setHours(0, 0, 0, 0);
      return date;
    },
    toStartOfDay(date) {
      const next = new Date(date);
      next.setHours(0, 0, 0, 0);
      return next;
    },
    diffDays(start, end) {
      const ms = 24 * 60 * 60 * 1000;
      return Math.round((end - start) / ms);
    },
    formatDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    },
    getTimelineMeta(row) {
      const totalDays = this.scheduleTimelineDays.length;
      if (!this.scheduleTimelineRange || totalDays === 0) {
        return { offset: 0, length: 0, remaining: 0, valid: false };
      }
      const start = this.parseDate(row.startDate);
      const end = this.parseDate(row.endDate);
      if (!start || !end || end < start) {
        return { offset: 0, length: 0, remaining: totalDays, valid: false };
      }
      const rangeStart = this.toStartOfDay(this.scheduleTimelineRange.start);
      let offset = this.diffDays(rangeStart, start);
      let length = this.diffDays(start, end) + 1;
      if (offset < 0) {
        length += offset;
        offset = 0;
      }
      if (offset >= totalDays || length <= 0) {
        return { offset: 0, length: 0, remaining: totalDays, valid: false };
      }
      if (offset + length > totalDays) {
        length = totalDays - offset;
      }
      const remaining = totalDays - offset - length;
      return { offset, length, remaining, valid: length > 0 };
    },
    getTimelineColor(index) {
      if (!this.scheduleTimelineColors.length) return '#2F80ED';
      return this.scheduleTimelineColors[index % this.scheduleTimelineColors.length];
    },
    riskChipClass(level) {
      switch (level) {
        case '상': return 'danger';
        case '중': return 'warning';
        case '하': return 'success';
        default: return 'line-type';
      }
    },
    formatRange(range) {
      if (!range || !range.length) return this.dash;
      const f = range[0] ? moment(range[0]).format('YYYY-MM-DD') : '';
      const t = range[1] ? moment(range[1]).format('YYYY-MM-DD') : '';
      return f && t ? `${f} ~ ${t}` : (f || t || this.dash);
    },
    formatPeriod(from, to) {
      if (!from && !to) return this.dash;
      const f = from ? moment(Number(from)).format('YYYY-MM-DD') : '';
      const t = to ? moment(Number(to)).format('YYYY-MM-DD') : '';
      return f && t ? `${f} ~ ${t}` : (f || t);
    },
    statusLabel(s) {
      switch (s) {
        case 'DRAFT': return '임시저장';
        case 'SUBMITTED': return '신청 완료 (담당자 검토 대기)';
        case 'NTREE_PENDING': return 'NTREE 결재 진행 중';
        case 'APPROVED': return '승인';
        case 'REJECTED': return '반려';
        default: return s || '-';
      }
    },
    statusTagClass(s) {
      switch (s) {
        case 'DRAFT': return 'line-type';
        case 'SUBMITTED': return 'warning';
        case 'NTREE_PENDING': return 'light-blue';
        case 'APPROVED': return 'success';
        case 'REJECTED': return 'danger';
        default: return 'line-type';
      }
    }
  }
};
</script>

<style scoped>
.wp-check-page {
  color: #333;
}
/* NDKC .tbl 기본 스타일을 그대로 사용하므로 별도 .summary-tbl 정의 제거 */
.sub-title {
  font-weight: 600;
  font-size: 13px;
  color: #333;
  border-bottom: 1px solid #e2e2e2;
  padding-bottom: 4px;
  margin-bottom: 8px;
}
.equip-card {
  border: 1px solid #e2e2e2;
  border-radius: 4px;
  padding: 10px 12px;
  margin-bottom: 10px;
  background: #fafbfc;
}
.equip-card-title {
  font-weight: 600;
  color: #333;
  margin-bottom: 6px;
}

/* 위치/위험작업/상태 표시는 NDKC `.tag` 컴포넌트 사용 — 별도 정의 없음 */

/* 허가서 상세 탭 — NDKC .nav-tabs 기본 스타일 사용, 패널만 패딩 추가 */
.wp-detail-tab-content {
  padding-top: 16px;
}
/* 탭이 많아 폭이 모자라면 라벨이 26px 탭 아래로 넘치던 문제 → 여러 단으로 줄바꿈, 라벨 한 줄 유지 */
.wp-hr-tabs.nav-tabs {
  flex-wrap: wrap;
  row-gap: 4px;
}
.wp-hr-tabs.nav-tabs .nav-item a {
  white-space: nowrap;
}

/* 넓은 표(공정표·위험성평가)가 카드를 넘칠 때 가로 스크롤 처리 */
.wp-table-scroll {
  overflow-x: auto;
  max-width: 100%;
}
/* 위험성평가 표는 고정 col 폭이 없어 좁으면 열이 뭉개짐 → 최소 폭 확보 후 스크롤 */
.wp-check-risk-tbl {
  min-width: 900px;
}

/* 예정 공정표 (gantt) */
.wp-timeline {
  table-layout: fixed;
  min-width: 40vw;
}
.wp-timeline th.wp-timeline-day {
  padding: 4px 0;
  text-align: center;
  border-left: 1px dotted #bdbdbd;
}
.wp-timeline td.wp-timeline-empty,
.wp-timeline td.wp-timeline-span {
  padding: 0;
  border-left: 1px dotted #bdbdbd;
}
.wp-timeline td.wp-timeline-empty {
  height: 30px;
}
.wp-timeline-bar {
  height: 14px;
  border-radius: 4px;
  margin: 6px 2px;
}
.wp-detail-pane > .sub-title:first-child {
  margin-top: 0;
}
</style>
